import React from 'react';

export interface TabItem {
  id: string;
  label: React.ReactNode;
  /** Shown as a small pill after the label, e.g. a result count. */
  count?: number;
  disabled?: boolean;
  /**
   * The panel this tab controls. Set it (or `panels` on the strip) only when
   * the page really renders a role="tabpanel" with that id; a dangling
   * aria-controls is worse than none.
   */
  panelId?: string;
  /** Hook for the guided tutorial; rendered as data-tutorial. */
  tutorialId?: string;
}

interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  /** Prefix for the generated tab / panel element ids. */
  idPrefix: string;
  /** Filters or buttons that belong on the same row, right of the tabs. */
  actions?: React.ReactNode;
  /** data-tutorial for the tablist itself. */
  tutorialId?: string;
  /** The page renders a role="tabpanel" with id tabPanelId(idPrefix, id) per tab. */
  panels?: boolean;
  className?: string;
}

const BASE = 'flex-1 min-h-[40px] whitespace-nowrap rounded-control px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signature disabled:opacity-50 disabled:cursor-not-allowed';
const SELECTED = 'bg-surface-subtle text-ink';
const IDLE = 'text-ink-muted hover:text-ink-secondary';

export const tabId = (prefix: string, id: string): string => `${prefix}-tab-${id}`;
export const tabPanelId = (prefix: string, id: string): string => `${prefix}-panel-${id}`;

/**
 * The one tab strip: a bordered pill row (the Knowledge Base design) with
 * the selected tab on the subtle surface and ink text. Arrow, Home and End
 * keys move between tabs. Pages pass items and, when they need them, the
 * row's right-hand actions; they never restyle the strip.
 */
export const Tabs: React.FC<TabsProps> = ({
  items,
  value,
  onChange,
  ariaLabel,
  idPrefix,
  actions,
  tutorialId,
  panels = false,
  className = '',
}) => {
  const focusTab = (id: string) => {
    onChange(id);
    window.requestAnimationFrame(() => document.getElementById(tabId(idPrefix, id))?.focus());
  };

  // Keys step from the pressed tab's own position to the nearest enabled
  // neighbour, wrapping, so they work even when focus sits on a disabled tab.
  // With every tab disabled there is no target and the strip stays inert, as
  // it is for the mouse.
  const stepFrom = (index: number, direction: 1 | -1): TabItem | undefined => {
    for (let n = 1; n <= items.length; n += 1) {
      const candidate = items[(index + direction * n + items.length * n) % items.length];
      if (!candidate.disabled) return candidate;
    }
    return undefined;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, id: string) => {
    const index = items.findIndex((item) => item.id === id);
    if (index === -1) return;
    let target: TabItem | undefined;
    if (event.key === 'ArrowRight') target = stepFrom(index, 1);
    if (event.key === 'ArrowLeft') target = stepFrom(index, -1);
    if (event.key === 'Home') target = items.find((item) => !item.disabled);
    if (event.key === 'End') target = [...items].reverse().find((item) => !item.disabled);
    if (!target || target.id === id) return;
    event.preventDefault();
    focusTab(target.id);
  };

  return (
    <div className={`mb-5 flex flex-wrap items-center justify-between gap-3 ${className}`.trim()}>
      <div className="max-w-full overflow-x-auto">
        <nav
          role="tablist"
          aria-label={ariaLabel}
          data-tutorial={tutorialId}
          className="flex w-max min-w-full gap-1 rounded-card border border-hairline bg-surface p-1"
        >
          {items.map((item) => {
            const selected = item.id === value;
            return (
              <button
                key={item.id}
                type="button"
                id={tabId(idPrefix, item.id)}
                role="tab"
                aria-selected={selected}
                aria-controls={item.panelId ?? (panels ? tabPanelId(idPrefix, item.id) : undefined)}
                tabIndex={selected ? 0 : -1}
                disabled={item.disabled}
                data-tutorial={item.tutorialId}
                onClick={() => { if (item.id !== value) onChange(item.id); }}
                onKeyDown={(event) => handleKeyDown(event, item.id)}
                className={`${BASE} ${selected ? SELECTED : IDLE}`}
              >
                {item.label}
                {item.count !== undefined && (
                  <span
                    data-testid={`tab-count-${item.id}`}
                    className={`ml-2 rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                      selected ? 'bg-surface text-ink-secondary' : 'bg-surface-subtle text-ink-muted'
                    }`}
                  >
                    {item.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
      {/* ml-auto keeps the actions on the right when they wrap below the tabs. */}
      {actions && <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    </div>
  );
};
